import type { CrmSyncDisplay, CrmSyncStatus, InvoicePaymentStatus, SettlementStatus } from '@neon-one/auctions-types';
import { httpClient } from '@/shell';

export type { CrmSyncDisplay, CrmSyncStatus };

export interface ReconcileInvoiceRow {
  invoiceId: number;
  invoiceUuid: string;
  // Human-readable identity, unique per event ("INV-1041"). The Invoice column,
  // the retry control's accessible name and the void notice all name it.
  invoiceNumber: string;
  invoicePaymentStatus: InvoicePaymentStatus;
  settlementStatus: SettlementStatus;
  bidderUuid: string;
  bidderId: number;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  registeredAt: string;
  // Stored CRM state is distinct from the server-derived payment readiness.
  syncStatus: CrmSyncStatus;
  syncDisplay: CrmSyncDisplay;
  retryEligible: boolean;
  syncedAt: string | null;
  lastAttemptedAt: string | null;
  lastError: string | null;
  retryCount: number;
  recordType: 'paid_invoice' | null;
}

export interface ReconcileSummary {
  total: number;
  synced: number;
  constituentSynced: number;
  pending: number;
  failed: number;
  permanentlyFailed: number;
}

export interface ReconcileListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  statuses?: CrmSyncDisplay[];
  sortBy?: 'syncStatus' | 'lastAttemptedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedReconcileResult {
  rows: ReconcileInvoiceRow[];
  summary: ReconcileSummary;
  pagination: { page: number; pageSize: number; total: number };
}

export interface BulkRetryResult {
  enqueuedMissing: number;
  retriedFailed: number;
  retriedPermanent: number;
  upgradedToPaidInvoice: number;
  retriedPending: number;
}

export interface CrmStatusResult {
  counts: {
    total: number;
    synced: number;
    constituentSynced: number;
    pending: number;
    failed: number;
    permanentlyFailed: number;
  };
}

// A void invoice enqueues nothing — the endpoint marks it permanently_failed
// and says so by returning zero. Callers read `enqueued` rather than assuming
// a retry is now in flight. /* §6 void can only permanently fail */
export interface SingleRetryResult {
  enqueued: number;
  invoiceId?: number;
}

const basePath = (auctionUuid: string): string =>
  `/admin/auctions/${encodeURIComponent(auctionUuid)}/crm`;

export const reconcileApi = {
  async listInvoices(auctionUuid: string, params: ReconcileListParams = {}): Promise<PaginatedReconcileResult> {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.pageSize !== undefined) query.set('pageSize', String(params.pageSize));
    if (params.search) query.set('search', params.search);
    if (params.statuses && params.statuses.length > 0) query.set('statuses', params.statuses.join(','));
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortOrder) query.set('sortOrder', params.sortOrder);

    const resp = await httpClient().get<PaginatedReconcileResult>(
      `${basePath(auctionUuid)}/reconciliation?${query.toString()}`,
    );
    return resp.data;
  },

  async triggerBulkRetry(auctionUuid: string): Promise<BulkRetryResult> {
    const resp = await httpClient().post<BulkRetryResult>(`${basePath(auctionUuid)}/reconcile`);
    return resp.data;
  },

  // Invoice-keyed, never bidder-keyed: the bidder route retries every one of
  // that person's failed orders, which is not what a row-level control offers.
  async retryInvoice(auctionUuid: string, invoiceUuid: string): Promise<SingleRetryResult> {
    const resp = await httpClient().post<SingleRetryResult>(
      `${basePath(auctionUuid)}/reconcile/invoice/${encodeURIComponent(invoiceUuid)}`,
    );
    return resp.data;
  },

  async retryInvoices(auctionUuid: string, invoiceUuids: string[]): Promise<{ enqueued: number }> {
    const resp = await httpClient().post<{ enqueued: number }>(
      `${basePath(auctionUuid)}/reconcile/batch`,
      { invoiceUuids },
    );
    return resp.data;
  },

  async getStatus(auctionUuid: string): Promise<CrmStatusResult> {
    const resp = await httpClient().get<CrmStatusResult>(`${basePath(auctionUuid)}/status`);
    return resp.data;
  },

  async exportCsv(auctionUuid: string): Promise<Blob> {
    const resp = await httpClient().get<Blob>(`${basePath(auctionUuid)}/reconciliation/export`, {
      responseType: 'blob',
    });
    return resp.data;
  },
};
